
import Promise from 'bluebird';
import map from 'lodash/map';

import firebaseApp from '../config/firebase';
import { handleException } from '../app';
import { TASK_ACTION_TYPES } from './constants';

import type { Dispatch, GetState } from '../types';
import type {
  CreateTaskObject,
  CreateSubTaskObject
} from './types';
import type Task from '../../models/Task';
import type SubTask from '../../models/SubTask';

export function createTask(params: CreateTaskObject): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.CREATE_TASK
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const task = {
        details: params.details,
        project: params.project,
        createdBy: currentUser.uid
      };
      const taskRef = await db.ref('tasks').push(task);
      dispatch({
        type: TASK_ACTION_TYPES.RECEIVE_TASK,
        payload: {
          task: {
            uid: taskRef.key,
            ...task
          }
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function createSubTask(params: CreateSubTaskObject): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.CREATE_SUBTASK
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const task = {
        details: params.details,
        project: params.project,
        parentTask: params.parentTask,
        createdBy: currentUser.uid,
        isComplete: false
      };
      const taskRef = await db.ref('subTasks').push(task);
      dispatch({
        type: TASK_ACTION_TYPES.RECEIVE_SUBTASK,
        payload: {
          subTask: {
            uid: taskRef.key,
            ...task
          }
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function deleteTask(params: DeleteTaskObject): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.DELETE_TASK
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await db.ref(`tasks/${params.uid}`).set(null);

      const subTasksRef = db
        .ref('subTasks')
        .orderByChild('parentTask')
        .equalTo(params.uid);
      await subTasksRef.on('value', (subTasks) => {
        const subTasksData = subTasks.toJSON();
        for (var subTask in subTasksData) {
          deleteSubTask({"uid": subTask})
        }
      });

      dispatch({
        type: TASK_ACTION_TYPES.RECEIVE_DELETED_TASK,
        payload: {
          taskUid: params.uid
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function deleteSubTask(subTask: SubTask): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.DELETE_SUBTASK
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await db.ref(`subTasks/${subTask.uid}`).set(null);
      dispatch({
        type: TASK_ACTION_TYPES.RECEIVE_DELETED_SUBTASK,
        payload: {
          subTask: {
            ...subTask
          }
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadTasks(projectId: string): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.FETCH_TASKS
    });
    try {
      const db = firebaseApp.database();
      const tasksRef = db.ref('tasks').orderByChild('project').equalTo(projectId);
      await tasksRef.on('value', tasks => {
        dispatch({
          type: TASK_ACTION_TYPES.RECEIVE_TASKS,
          payload: {
            tasks: map(tasks.toJSON(), (task, key) => ({
              uid: key,
              ...task
            })) || []
          }
        });
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadSubTasks(taskId: string): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.FETCH_SUBTASKS
    });
    try {
      const db = firebaseApp.database();
      const subTasksRef = db.ref('subTasks').orderByChild('parentTask').equalTo(taskId);
      subTasksRef.on('value', subTasks => {
        dispatch({
          type: TASK_ACTION_TYPES.RECEIVE_SUBTASKS,
          payload: {
            subTasks: map(subTasks.toJSON(), (subtask, key) => ({
              uid: key,
              ...subtask
            })) || []
          }
        });
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function setTaskCompleteness(task: Task, isComplete: boolean): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.TOGGLE_TASK_COMPLETENESS
    });
    try {
      const db = firebaseApp.database();
      const ref = db.ref(`tasks/${task.id}/isComplete`);
      await ref.set(isComplete);
      dispatch({
        type: TASK_ACTION_TYPES.RECEIVE_TOGGLED_TASK_COMPLETENESS,
        payload: {
          task: {
            ...task.toJS(),
            isComplete
          }
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function setSubTaskCompleteness(subTask: SubTask, isComplete: boolean): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TASK_ACTION_TYPES.TOGGLE_SUBTASK_COMPLETENESS
    });
    try {
      const db = firebaseApp.database();
      const ref = db.ref(`subTasks/${subTask.id}/isComplete`);
      await ref.set(isComplete);
      dispatch({
        type: TASK_ACTION_TYPES.RECEIVE_TOGGLED_SUBTASK_COMPLETENESS,
        payload: {
          subTask: {
            ...subTask.toJS(),
            isComplete
          }
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}



// WEBPACK FOOTER //
// ./src/redux/tasks/actions.js